import React, { useMemo } from 'react'

import ButtonSelector from '../ButtonSelector'

import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'

import './TrackAttributesSelector.scss'

const CLASS = 'insights-portal-track-attributes-selector'

type TrackAttributeSelectorProps = {
  className?: string
  trackTarget?: 'attribute' | 'entity'
  trackAttributes?: Record<string, unknown>
  attributes?: Record<string, { text: string; key: string; target: 'attribute' | 'entity' }>
  onTrackAttributesChange?: (value: { trackAttributes: Record<string, unknown> }) => void
}

export const TrackAttributesSelector: React.FC<TrackAttributeSelectorProps> = ({
  className: propsClassName,
  trackTarget: propsTrackTarget = 'entity',
  trackAttributes: propsTrackAttributes = {},
  attributes: propsAttributes = {} as TrackAttributeSelectorProps['attributes'],
  onTrackAttributesChange: propsOnTrackAttributesChange
}) => {
  const className = propsClassName || CLASS

  const trackTarget = propsTrackTarget || 'entity'
  const trackAttributes = propsTrackAttributes || {}
  const attributes = useMemo(() => propsAttributes || {}, [propsAttributes])

  const attributeKey = trackAttributes[trackTarget]
  const emptyText = 'entity' === trackTarget ? 'All' : ''
  const attribute: string = (Object.values(attributes).find(v => v.key === attributeKey) || {}).text || emptyText

  const trackAttributesOptions = useMemo(() => {
    const items = Object.values(attributes)
      .filter(v => v.target === trackTarget)
      .map(v => {
        return { code: v.key, name: decode(v.text) }
      })
    if ('entity' === trackTarget) {
      items.unshift({ code: null, name: 'All' })
    }
    return items
  }, [attributes, trackTarget])

  const onTrackAttributesChange = (event: { value: { code: string | null } }) => {
    if (propsOnTrackAttributesChange && event.value.code !== attributeKey) {
      const newTrackAttributes = {
        ...trackAttributes,
        [trackTarget]: event.value.code
      }
      propsOnTrackAttributesChange({ trackAttributes: newTrackAttributes })
    }
  }

  return (
    <ButtonSelector
      className={className}
      options={trackAttributesOptions}
      label={decode(attribute)}
      invalid={!attribute}
      onChange={onTrackAttributesChange}
    />
  )
}
TrackAttributesSelector.displayName = 'TrackAttributeSelector'
